import axios, { AxiosInstance } from 'axios'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { Configuration } from 'mvppoker-portal-common/api/gen/configuration'
import toast from 'react-hot-toast'
import { RootState, store } from '../state/AppStore'
import version from '../config/version.json'
import apiKeys from '../config/apiKeys.json'
import ConsoleLogger from './ConsoleLogger'

const { host } = apiKeys
const CLIENT_NAME = 'web-poker'
const CLIENT_VERSION = version.apiVersion

export interface AxiosError<T> {
	response: {
		status: number
		statusText: string
		data: T
	}
}

export interface DefaultApiError {
	error?: string
	message: string
	statusCode: number
}

export const ApiError = (oErr: any): DefaultApiError | undefined => {
	const { response } = oErr
	const data = response?.data
	const error = data?.error
	const message = data?.message
	const statusCode = data?.statusCode

	if (message !== undefined && statusCode !== undefined) {
		return { error, message, statusCode }
	}
}

export const handleError = (oErr: any): void => {
	const apiErr = ApiError(oErr)
	let message: string

	if (apiErr) {
		message = apiErr.message
	} else {
		message = 'An unknown error occured'
		console.dir(oErr)
	}

	toast.error(message)
}

export type APIConfigType = {
	host: string
	token?: string
	timeout: number
	headers: Record<string, string>
}

export interface IApi<T> {
	new (configuration?: Configuration, basePath?: string, axios?: AxiosInstance): T
}

export function createClient({ host, token, timeout, headers }: APIConfigType): AxiosInstance {
	if (token) {
		headers.Authorization = `Bearer ${token}`
	} else {
		delete headers.Authorization
	}

	const ax = axios.create({
		baseURL: host,
		timeout,
		headers,
		responseType: 'json',
	})
	ax.interceptors.request.use((req) => {
		ConsoleLogger.Network(`=> ${req.url}`)
		return req
	})
	ax.interceptors.response.use((res) => {
		const url = res.config.url ?? 'unknown url'
		const code = res.status
		ConsoleLogger.Network(`${code} <= ${url}`)
		return res
	})

	return ax
}

/// non-reactive useApi equivalent
export function getApi<T>(ApiClass: IApi<T>) {
	const timeout = 1_000 * 60 * 3
	const headers = {
		'client-name': CLIENT_NAME,
		'client-version': CLIENT_VERSION,
	}
	const token = store.getState().activeUser.tokens?.idToken

	const config: APIConfigType = { token, host, timeout, headers }

	const client = createClient(config)
	return new ApiClass(undefined, host, client)
}

export function useApi<T>(ApiClass: IApi<T>) {
	const timeout = 1_000 * 60 * 3
	const headers = {
		'client-name': CLIENT_NAME,
		'client-version': CLIENT_VERSION,
	}
	const token = useSelector((root: RootState) => root.activeUser.tokens?.idToken)

	const config: APIConfigType = { token, host, timeout, headers }

	const api = useMemo(() => {
		const client = createClient(config)
		return new ApiClass(undefined, host, client)
	}, [config])

	return api
}
